import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Box, Button, CircularProgress, IconButton, Popover, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CachePolicies } from "use-http";

import { BiotechIcon } from "assets/icons";
import { UserLimitModal } from "components/account/UserLimitModal/UserLimitModal";
import { CallToActionButton } from "components/common/CallToActionButton";
import { SubNavBar } from "components/common/SubNavBar";
import { DEFAULT_EXPLORE_PAGE_PREDICTOR_CONFIG, JANZU_URL } from "config/config";
import { NO_RETRIES } from "config/constants";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useFetchCustom } from "hooks/useFetchCustom";
import { useModalLimit } from "hooks/useModalLimit/useModalLimit";
import { usePostEvent } from "hooks/usePostEvent";
import { SelectDetector } from "new-components/selectDetector/SelectDetector";
import { Typography } from "new-components/typography/Typography";
import { getUserState, setExploreDetector } from "store/slices/Auth/authSlice";
import { DEFAULT_CONFIDENCE_THRESHOLD, Detector, type CreateDetectorInput } from "types/detectors";
import { User } from "types/users";
import { isErrorStatusCode, isSuccessfulStatusCode, isTimeoutStatusCode } from "utils/statusCheck";

const VALIDATION_ERROR = "Prompt cannot be empty";

export interface DemoSubNavbarProps {
  detector: Detector | undefined;
  loadingPredictors: boolean;
  setIsRestartModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onDetectorSelect: (detector: Detector) => void;
  shouldShowSelector: boolean;
}

export function DemoSubNavbar(props: DemoSubNavbarProps) {
  const newPromptRef = useRef<string>("");
  const { enqueueSnackbar } = useSnackbar();
  const { sendErrorEvent } = usePostEvent();

  // User information
  const userData: User = useAppSelector(getUserState);
  const [newPrompt, setNewPrompt] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { detector, loadingPredictors, setIsRestartModalOpen, onDetectorSelect, shouldShowSelector } = props;
  const { isBlocked, modalLimit, handleOpenModalLimit, handleCloseModalLimit } = useModalLimit("detectorIsBlocked");
  const open = Boolean(anchorEl);

  const dispatch = useAppDispatch();

  // set up the createPredictor function
  const { post: createDetector } = useFetchCustom<Detector>(
    `${JANZU_URL}device-api/predictors`,
    {
      cachePolicy: CachePolicies.NO_CACHE,
      retries: NO_RETRIES,
      retryOn: [],
      interceptors: {
        response: async ({ response }) => {
          const res = response;
          if (isSuccessfulStatusCode(res.status)) {
            onDetectorSelect(res.data);
          }

          if (isErrorStatusCode(res.status)) {
            let errorMessage = "";
            if (response.data === "The user already has an explore detector.") {
              errorMessage = response.data;
            } else {
              errorMessage = response.statusText || "We're sorry, but an error has occurred";
            }
            enqueueSnackbar(errorMessage, { variant: "error" });
          }

          if (isTimeoutStatusCode(res.status)) {
            sendErrorEvent(res.url);
            enqueueSnackbar("We're sorry, but an error has occurred. Please try reloading the page", {
              variant: "error",
            });
          }
          return res;
        },
      },
    }
  );

  const validatePrompt = (prompt: string) => {
    if (prompt === "") throw new Error(VALIDATION_ERROR);
  };

  async function onSave(newDetectorBody: CreateDetectorInput) {
    validatePrompt(newDetectorBody.label_prompt || "");

    const newDetector = await createDetector(newDetectorBody);

    if (newDetector) {
      onDetectorSelect(newDetector);
      dispatch(setExploreDetector(newDetector.id));
    }
    setAnchorEl(null);
  }

  const openPredictorPopover = (event: React.MouseEvent<HTMLElement>) => {
    if (isBlocked) {
      handleOpenModalLimit();
      return;
    }

    setAnchorEl(event.currentTarget);
    setNewPrompt("");
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const showDetectorName = () => {
    // Apply only for non superadmin and sciDuck users
    if (loadingPredictors) {
      return <CircularProgress color="inherit" sx={{ width: "25px!important", height: "25px!important" }} />;
    }
    return (
      <Typography variant="subtitle" fontWeight="bold" textTransform="uppercase">
        {detector?.name}
      </Typography>
    );
  };

  const handleCreateNewExploreDetector = (labelPrompt?: string, confidenceThreshold?: number) => {
    // This method helps to create a detector from:
    //   popup (for Sciduck or superuser)
    //   modal (for general users)
    const createPredictorInput: CreateDetectorInput = {
      ...DEFAULT_EXPLORE_PAGE_PREDICTOR_CONFIG,
      label_prompt: labelPrompt || newPrompt,
      confidence_threshold: confidenceThreshold || DEFAULT_CONFIDENCE_THRESHOLD,
      is_explore: true,
    };
    onSave(createPredictorInput);
  };

  const detailDetectorLink = detector ? `/reef/detectors/${detector.id}` : "/reef";

  return (
    <>
      <UserLimitModal type="detector" isModalOpen={modalLimit} handleCloseModal={handleCloseModalLimit} />
      <SubNavBar>
        <div className="flex flex-row w-full justify-between">
          <div
            className={`flex flex-row gap-4 items-center w-full ${userData.allow_explore_selection ? "justify-between" : ""
              } md:w-auto`}
          >
            <Link to={detailDetectorLink} className="flex gap-4 no-underline">
              <IconButton aria-label="back" className="p-0">
                <BiotechIcon color="#ffffff" />
              </IconButton>

              <IconButton aria-label="back" className="p-0">
                <ArrowBackIosNewIcon className="text-white" />
              </IconButton>
            </Link>

            {userData.allow_explore_selection && shouldShowSelector ? (
              <>
                <SelectDetector
                  detectorId={detector?.id || ""}
                  detectorName={detector?.name || ""}
                  loadingDetectorData={loadingPredictors}
                  onExploreDetectorSelect={onDetectorSelect}
                  detectorQuery={detector?.label_prompt}
                />
                <div>
                  <CallToActionButton
                    buttonText="Create Detector"
                    onClick={openPredictorPopover}
                    IconComponent={AddIcon}
                  />

                  <Popover
                    open={open}
                    sx={{ width: "600px", marginTop: "10px" }}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                  >
                    <Box className="bg-white flex flex-col justify-between gap-2 px-12 py-4 rounded shadow-lg min-w-[94vw] md:min-w-[500px]">
                      <TextField
                        variant="outlined"
                        size="small"
                        autoFocus
                        label="Enter Query"
                        onChange={(e) => {
                          setNewPrompt(e.target.value);
                          newPromptRef.current = e.target.value;
                        }}
                        fullWidth
                      />
                      <Box className="flex justify-center gap-2">
                        <Button
                          onClick={handleClose}
                          size="small"
                          variant="contained"
                          color="primary"
                          style={{ fontSize: "12px", padding: "4px 8px" }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => handleCreateNewExploreDetector()}
                          style={{ fontSize: "12px", padding: "4px 8px" }}
                        >
                          Save
                        </Button>
                      </Box>
                    </Box>
                  </Popover>
                </div>
              </>
            ) : (
              showDetectorName()
            )}
          </div>
          {detector && (
            <Button
              onClick={() => setIsRestartModalOpen(true)}
              className="self-center bg-white bg-opacity-20 px-[6px] py-[4px] hidden md:flex"
            >
              <Typography textTransform="capitalize" className="hidden md:block text-white mr-2">
                Reset detector
              </Typography>
              <RestartAltIcon className="text-white" />
            </Button>
          )}
        </div>
      </SubNavBar>
    </>
  );
}
